import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Seo from '../components/Seo';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { settingsService, type PublicSettings } from '../services/settings.service';
import { authService, publicService } from '../services';
import type { Plan } from '../types';
import { Loader2, Briefcase, Users, Check, X, Zap, Star, Rocket, HelpCircle } from 'lucide-react';

export default function HowItWorks() {
  const [settings, setSettings] = useState<PublicSettings>({});
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(true);
  const [plansLoading, setPlansLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const data = await settingsService.getPublicSettings();
        if (data?.faq_items && Array.isArray(data.faq_items)) {
          const firstItem = data.faq_items[0];
          if (firstItem && 'questions' in firstItem && Array.isArray((firstItem as any).questions)) {
            data.faq_items = data.faq_items.flatMap((category: any) => category.questions || []);
          }
        }
        setSettings(data || {});
      } finally {
        setLoading(false);
      }
    })();

    (async () => {
      try {
        const data = await publicService.getPlans().catch(() => []);
        setPlans(data);
      } finally {
        setPlansLoading(false);
      }
    })();
  }, []);

  const isCompany = authService.isAuthenticated() && authService.isCompany();

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  const getIconForPlan = (plan: Plan, index: number) => {
    const name = plan.name.toLowerCase();
    if (name.includes('premium') || name.includes('pro')) return Rocket;
    if (name.includes('destaque') || name.includes('plus')) return Star;
    return Zap;
  };

  const isPopular = (plan: Plan, index: number) => {
    const name = plan.name.toLowerCase();
    return name.includes('destaque') || index === 1;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Seo
        title="Como Funciona - Vagas Brusque"
        description="Entenda como usar o Vagas Brusque e conheça os tipos de vaga para anunciar no portal."
        canonical={window.location.origin + '/como-funciona'}
        ogImage={window.location.origin + '/logo-vagas.svg'}
      />
      <Header />
      <main>
        <section className="py-16 bg-white border-b">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="inline-flex items-center justify-center rounded-lg bg-gradient-to-r from-brand-400 to-brand-700 px-6 py-3 text-white font-bold shadow-lg mb-5">
                Como Funciona
              </h1>
              <p className="text-gray-600">Entenda como usar o portal, publicar vagas e escolher o melhor tipo de anúncio</p>
            </div>
          </div>
        </section>

        <section className="py-12">
          <div className="container mx-auto px-4">
            <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-white border rounded-lg p-6 md:p-8">
                <div className="flex items-center gap-3 mb-4">
                  <div className="bg-brand-50 text-brand-800 rounded-lg p-3"><Briefcase className="w-6 h-6" /></div>
                  <h2 className="text-gray-900">Para Empresas</h2>
                </div>
                {loading ? (
                  <div className="text-center py-12">
                    <Loader2 className="size-8 animate-spin text-brand-600 mx-auto mb-3" />
                    <p className="text-gray-600">Carregando conteúdo...</p>
                  </div>
                ) : settings.how_it_works_companies ? (
                  <div className="prose max-w-none whitespace-pre-wrap leading-relaxed text-gray-800">
                    {settings.how_it_works_companies}
                  </div>
                ) : (
                  <p className="text-gray-600">Conteúdo não disponível ainda.</p>
                )}
              </div>

              <div className="bg-white border rounded-lg p-6 md:p-8">
                <div className="flex items-center gap-3 mb-4">
                  <div className="bg-brand-50 text-brand-800 rounded-lg p-3"><Users className="w-6 h-6" /></div>
                  <h2 className="text-gray-900">Para Candidatos</h2>
                </div>
                {loading ? (
                  <div className="text-center py-12">
                    <Loader2 className="size-8 animate-spin text-brand-600 mx-auto mb-3" />
                    <p className="text-gray-600">Carregando conteúdo...</p>
                  </div>
                ) : settings.how_it_works_candidates ? (
                  <div className="prose max-w-none whitespace-pre-wrap leading-relaxed text-gray-800">
                    {settings.how_it_works_candidates}
                  </div>
                ) : (
                  <p className="text-gray-600">Conteúdo não disponível ainda.</p>
                )}
              </div>
            </div>
          </div>
        </section>

        <section className="bg-gradient-to-br from-brand-400 to-brand-700 text-white py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-white mb-6">Anuncie sua Vaga</h2>
              <p className="text-xl text-brand-100 mb-8">
                Escolha o tipo de vaga ideal para encontrar os melhores profissionais em Brusque
              </p>
              <div className="flex items-center justify-center gap-2 text-sm text-brand-100">
                <Check className="size-4" />
                <span>Pague por vaga - Sem mensalidade - Suporte incluído</span>
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            {plansLoading ? (
              <div className="text-center py-12">
                <Loader2 className="size-8 animate-spin text-brand-600 mx-auto mb-4" />
                <p className="text-gray-600">Carregando tipos de vagas...</p>
              </div>
            ) : plans.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-gray-500">Nenhum tipo de vaga disponível no momento.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
                {plans.filter(p => p.is_active).map((plan, index) => {
                  const Icon = getIconForPlan(plan, index);
                  const popular = isPopular(plan, index);
                  return (
                    <Card
                      key={plan.id}
                      className={`relative overflow-hidden ${popular ? 'border-brand-300 shadow-xl scale-105' : 'border-gray-200'}`}
                    >
                      {popular && (
                        <div className="absolute top-0 right-0 bg-brand-300 text-brand-900 px-4 py-1 text-sm font-medium">
                          Mais Popular
                        </div>
                      )}

                      <div className="p-8">
                        <div className="flex items-center gap-3 mb-4">
                          <div className={`${popular ? 'bg-brand-300' : 'bg-gray-200'} p-3 rounded-lg`}>
                            <Icon className={`size-6 ${popular ? 'text-white' : 'text-gray-600'}`} />
                          </div>
                          <h3 className="text-xl font-semibold text-gray-900">{plan.name}</h3>
                        </div>

                        <p className="text-gray-600 mb-6">{plan.description}</p>

                        <div className="mb-6">
                          {plan.discount_percent > 0 && (
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-lg text-gray-400 line-through">{formatCurrency(plan.price)}</span>
                              <span className="bg-red-500 text-white text-xs font-bold px-2 py-1 rounded">
                                {plan.discount_percent}% OFF
                              </span>
                            </div>
                          )}
                          <div className="flex items-baseline gap-2">
                            <span className="text-4xl font-bold text-gray-900">
                              {formatCurrency(plan.price * (1 - (plan.discount_percent || 0) / 100))}
                            </span>
                            <span className="text-gray-600">/{plan.duration_days} dias</span>
                          </div>
                        </div>

                        <Link to={isCompany ? '/empresa/vaga/nova' : '/login'} className="block mb-6">
                          <Button className={`w-full ${popular ? 'bg-brand-300 hover:bg-brand-500 text-white' : ''}`} variant={popular ? 'default' : 'outline'} size="lg">
                            {isCompany ? 'Anunciar vaga' : 'Entrar para anunciar'}
                          </Button>
                        </Link>

                        {plan.features && plan.features.length > 0 && (
                          <div className="space-y-3">
                            {plan.features.map((raw, idx) => {
                              const line = raw?.trim?.() ?? '';
                              if (!line) return null;
                              const isIncluded = /^(✅|✔|[Vv]\b)/.test(line);
                              const isExcluded = /^(❌|✖|[Xx]\b)/.test(line);
                              const text = (isIncluded || isExcluded)
                                ? line.replace(/^(?:✅|✔|❌|✖|[VvXx])\s*[:\-]?\s*/, '')
                                : line;
                              if (!isIncluded && !isExcluded) {
                                return (
                                  <div key={idx} className="pt-2">
                                    <span className="text-gray-900 text-sm font-medium">{text}</span>
                                  </div>
                                );
                              }
                              return (
                                <div key={idx} className="flex items-start gap-3">
                                  {isIncluded ? (
                                    <Check className="size-5 text-brand-600 flex-shrink-0 mt-0.5" />
                                  ) : (
                                    <X className="size-5 text-red-600 flex-shrink-0 mt-0.5" />
                                  )}
                                  <span className={`text-sm ${isExcluded ? 'text-gray-600' : 'text-gray-700'}`}>{text}</span>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>
        </section>

        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="inline-flex items-center justify-center rounded-lg bg-gradient-to-r from-brand-400 to-brand-700 px-6 py-3 text-white font-bold shadow-lg">
                  Perguntas Frequentes
                </h2>
              </div>

              {loading ? (
                <div className="text-center py-8 text-gray-600">Carregando...</div>
              ) : (Array.isArray(settings.faq_items) && settings.faq_items.length > 0) ? (
                <div className="space-y-6">
                  {settings.faq_items.map((item, idx) => (
                    <Card className="p-6" key={idx}>
                      <div className="flex items-start gap-4">
                        <HelpCircle className="size-6 text-brand-600 flex-shrink-0" />
                        <div>
                          <h3 className="font-semibold text-gray-900 mb-2">{item.question}</h3>
                          <p className="text-gray-700 whitespace-pre-wrap">{item.answer}</p>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-600">Em breve adicionaremos perguntas frequentes.</div>
              )}
            </div>
          </div>
        </section>

        <section className="py-16 bg-gradient-to-br from-brand-400 to-brand-700 text-white">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-white mb-4">Pronto para contratar?</h2>
              <p className="text-xl text-brand-100 mb-8">
                Publique sua vaga hoje e encontre os melhores talentos de Brusque
              </p>
              <Link to="/empresa/vaga/nova">
                <Button size="lg" className="bg-white text-brand-700 hover:bg-gray-100">
                  Anunciar minha vaga
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
